'use client';

import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

export interface UserDocument {
  id?: string;
  userProfileId: string;
  documentType: string;
  documentName: string;
  fileUrl: string;
  fileSize?: number;
  mimeType?: string;
  status?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  adminNotes?: string;
  uploadedAt?: string;
  updatedAt?: string;
}

export const userDocumentService = {
  async uploadDocument(file: File, userProfileId: string, documentType: string) {
    const supabase = createClient();

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${documentType}_${Date.now()}.${fileExt}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('user-documents')
        .upload(fileName, file);

      if (uploadError) throw new Error(uploadError.message);

      const { data: { publicUrl } } = supabase.storage
        .from('user-documents')
        .getPublicUrl(fileName);

      const { data, error } = await supabase
        .from('user_documents')
        .insert({
          user_profile_id: userProfileId,
          document_type: documentType,
          document_name: file.name,
          file_url: publicUrl,
          file_size: file.size,
          mime_type: file.type,
          status: 'pending',
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return {
        id: data.id,
        userProfileId: data.user_profile_id,
        documentType: data.document_type,
        documentName: data.document_name,
        fileUrl: data.file_url,
        fileSize: data.file_size,
        mimeType: data.mime_type,
        status: data.status,
        uploadedAt: data.uploaded_at,
      };
    } catch (error: any) {
      throw error;
    }
  },

  async getDocumentsByProfileId(userProfileId: string): Promise<UserDocument[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('user_documents')
        .select('*')
        .eq('user_profile_id', userProfileId)
        .order('uploaded_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return data.map((doc: any) => ({
        id: doc.id,
        userProfileId: doc.user_profile_id,
        documentType: doc.document_type,
        documentName: doc.document_name,
        fileUrl: doc.file_url,
        fileSize: doc.file_size,
        mimeType: doc.mime_type,
        status: doc.status,
        reviewedBy: doc.reviewed_by,
        reviewedAt: doc.reviewed_at,
        adminNotes: doc.admin_notes,
        uploadedAt: doc.uploaded_at,
        updatedAt: doc.updated_at,
      }));
    } catch (error: any) {
      throw error;
    }
  },

  async deleteDocument(documentId: string, fileUrl: string) {
    const supabase = createClient();

    try {
      const fileName = fileUrl.split('/').slice(-2).join('/');

      const { error: storageError } = await supabase.storage
        .from('user-documents')
        .remove([fileName]);

      if (storageError) throw new Error(storageError.message);

      const { error } = await supabase
        .from('user_documents')
        .delete()
        .eq('id', documentId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return { success: true };
    } catch (error: any) {
      throw error;
    }
  },
};